/**
 * CROSS_VOL — Crossing Volume Overlay
 *
 * Renders amber bars inside vol_pane showing the institutional/HNW crossing
 * volume for each bar.  Visibility is toggled via the VOL gear modal; the
 * bars are always registered and calculated so toggling is instant (no chart
 * re-creation needed).
 *
 * Draw pattern: bar from y=0 to y=crossVolume, sharing the vol_pane y-axis.
 */

import { IndicatorSeries, type IndicatorTemplate } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';

type CrossVolRow = { crossVolume: number };

type DrawParams = {
  ctx:          CanvasRenderingContext2D;
  visibleRange: { from: number; to: number };
  xAxis:        { convertToPixel: (i: number) => number };
  yAxis:        { convertToPixel: (v: number) => number };
  indicator:    { result: CrossVolRow[] };
  barSpace:     { bar: number; gapBar: number };
};

const crossVol: IndicatorTemplate<CrossVolRow> = {
  name:       'CROSS_VOL',
  shortName:  'XVol',
  series:     IndicatorSeries.Normal,
  shouldOhlc: false,

  // Hidden line figure — feeds the crosshair legend value; actual rendering
  // is done manually in draw() which always returns true to suppress the line.
  figures: [
    { key: 'crossVolume', title: 'XVOL: ', type: 'line' },
  ],

  calc: (dataList) =>
    dataList.map((bar) => ({
      crossVolume: (bar as unknown as { crossVolume?: number | null }).crossVolume ?? 0,
    })),

  draw: (params: unknown): boolean => {
    const {
      ctx,
      visibleRange,
      xAxis,
      yAxis,
      barSpace,
      indicator,
    } = params as DrawParams;

    // Returning true prevents KLineCharts from drawing the default figure line.
    if (!useChartStore.getState().crossVolumeEnabled) return true;

    const barW  = Math.max(1, barSpace.gapBar);
    const zeroY = yAxis.convertToPixel(0);

    ctx.save();
    ctx.fillStyle  = '#F59E0B'; // amber-400
    ctx.globalAlpha = 0.85;

    for (let i = visibleRange.from; i <= visibleRange.to; i++) {
      const row = indicator.result[i];
      if (!row?.crossVolume) continue;

      const x    = xAxis.convertToPixel(i);
      const topY = yAxis.convertToPixel(row.crossVolume);
      const h    = zeroY - topY;
      if (h <= 0) continue;

      ctx.fillRect(x - barW / 2, topY, barW, h);
    }

    ctx.restore();
    return true;
  },
};

export default crossVol;
